import { FetchArgs } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { datadogRum } from '@/lib/DatadogService';

import { baseQueryType, QRes } from '@/resiliency/util';

function joinUrl(base: string, path?: string) {
  if (!path) return base;
  if (/^https?:\/\//i.test(path)) return path;
  if (base.endsWith('/') && path.startsWith('/')) return base + path.slice(1);
  if (!base.endsWith('/') && !path.startsWith('/')) return `${base}/${path}`;
  return base + path;
}

/**
 * Removes query string from a URL
 * @param url - The URL string to process
 * @returns {string} URL string with query parameters removed
 */
function removeQueryString(url: string): string {
  const questionMarkIndex = url.indexOf('?');
  return questionMarkIndex >= 0 ? url.substring(0, questionMarkIndex) : url;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Get a concise error message  */
function toErrorMessage(err: unknown): string {
  if (err == null) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message || err.name || 'Error';

  if (isRecord(err)) {
    const maybeError = err['error'];
    if (typeof maybeError === 'string') return maybeError;

    const maybeData = err['data'];
    if (typeof maybeData === 'string') return maybeData;
    if (isRecord(maybeData) || Array.isArray(maybeData)) {
      try {
        return JSON.stringify(maybeData);
      } catch {
        // fallback below
      }
    }
  }

  try {
    return JSON.stringify(err);
  } catch {
    return 'Unserializable error';
  }
}

/** Try to extract a status code/tag for telemetry */
function toStatus(err: unknown): string | number | undefined {
  if (!isRecord(err)) return undefined;

  const status = err['status'];
  if (typeof status === 'string' || typeof status === 'number') return status;

  const originalStatus = err['originalStatus'];
  if (typeof originalStatus === 'string' || typeof originalStatus === 'number')
    return originalStatus;

  return undefined;
}

/**
 * Wraps a base query function with a timeout mechanism, aborting requests that exceed the specified duration.
 *
 * @param baseUrl - The base URL to prepend to all requests.
 * @param baseQuery - The base query function to execute the request.
 * @param timeoutMs - Optional timeout in milliseconds. If not provided, uses `VITE_CLIENT_API_TIMEOUT_MS` from environment or defaults to 60000ms.
 * @returns A new base query function that enforces the timeout policy.
 *
 * @remarks
 * - If `timeoutMs` is less than or equal to 0, the timeout is disabled.
 * - Aborted requests due to timeout return a `TIMEOUT_ERROR`.
 * - Aborted requests due to external cancellation return a `FETCH_ERROR`.
 * - All errors are tracked via `appInsights.trackException`.
 * - The original abort signal is linked to the internal controller to support external cancellation.
 */
export const baseQueryWithTimeout = (
  baseUrl: string,
  baseQuery: baseQueryType,
  timeoutMs?: number
): baseQueryType => {
  timeoutMs ??= Number(import.meta.env.VITE_CLIENT_API_TIMEOUT_MS) || 60000;

  const BaseQueryWithTimeout: baseQueryType = async (...args) => {
    const [arg, api, extraOptions] = args;
    const fetchArgs: FetchArgs = typeof arg === 'string' ? { url: arg } : { ...(arg as FetchArgs) };

    const fullUrl = joinUrl(baseUrl, fetchArgs.url ?? '');
    const urlWithoutQuery = removeQueryString(fullUrl);

    const controller = new AbortController();
    let cleanupOrigSignal: (() => void) | undefined;

    // Link original signal (if provided) to our controller so external aborts still cancel the request
    if (fetchArgs.signal) {
      const orig = fetchArgs.signal as AbortSignal;
      if (orig.aborted) {
        controller.abort();
      } else {
        const onAbort = () => controller.abort();
        orig.addEventListener('abort', onAbort);
        cleanupOrigSignal = () => orig.removeEventListener('abort', onAbort);
      }
    }
    fetchArgs.signal = controller.signal;

    // If timeoutMs <= 0 we treat that as "disable timeout"
    const hasTimeout = timeoutMs > 0;

    // timeout error is only meaningful when a timeout is active
    let timeoutError: FetchBaseQueryError | undefined = undefined;
    if (hasTimeout) {
      timeoutError = {
        status: 'TIMEOUT_ERROR',
        error: `Request exceeded ${timeoutMs}ms`
      };
    }

    // baseQueryPromise resolves to QueryReturnValue<..., FetchBaseQueryError, ...>
    const baseQueryPromise: Promise<QRes> = (async (): Promise<QRes> => {
      try {
        const res = await baseQuery(fetchArgs, api, extraOptions);
        return res as QRes;
      } catch (err) {
        // If abort happened, map it to timeout error only if timeout is active.
        if (err instanceof Error && err.name === 'AbortError') {
          if (hasTimeout && timeoutError) {
            return { error: timeoutError };
          }
          // Non-timeout abort (e.g. external cancellation) -> return a distinct fetch error
          const fetchErr: FetchBaseQueryError = {
            status: 'FETCH_ERROR',
            error: 'Request was aborted'
          };
          return { error: fetchErr };
        }

        // Turn thrown errors into FetchBaseQueryError
        const fetchErr: FetchBaseQueryError = {
          status: 'FETCH_ERROR',
          error: toErrorMessage(err)
        };
        return { error: fetchErr };
      }
    })();

    // Create timeout promise only if timeout is enabled
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let timeoutPromise: Promise<QRes> | undefined;
    if (hasTimeout) {
      timeoutPromise = new Promise(resolve => {
        timeoutId = setTimeout(() => {
          try {
            controller.abort();
          } catch (abortErr) {
            const ex = abortErr instanceof Error ? abortErr : new Error(String(abortErr));
            datadogRum.addError(ex, { url: urlWithoutQuery, context: 'controller.abort' });
          }
          // resolve with the timeout-shaped error
          resolve({ error: timeoutError! });
        }, timeoutMs);
      });
    }

    // Race only if timeout is enabled; otherwise await the base query directly
    let result: QRes;
    if (hasTimeout) {
      result = await Promise.race<QRes>([baseQueryPromise, timeoutPromise!]);
    } else {
      result = await baseQueryPromise;
    }

    if (typeof timeoutId !== 'undefined') clearTimeout(timeoutId);
    if (cleanupOrigSignal) cleanupOrigSignal();

    // Timeout-specific handling (only possible when hasTimeout === true)
    if ('error' in result && result.error?.status === 'TIMEOUT_ERROR') {
      const msg = `[Timeout Policy] ${toErrorMessage(result.error)}`;
      // DEBUG
      //console.warn(`[RTK timeout] ${msg} → ${urlWithoutQuery}`);

      datadogRum.addError(new Error(msg), {
        url: urlWithoutQuery,
        status: 'TIMEOUT_ERROR',
        timeoutMs: String(timeoutMs)
      });
      return { error: timeoutError! };
    }

    // Other error
    if ('error' in result && result.error) {
      const err = result.error;
      const msg = toErrorMessage(err);
      const status = toStatus(err);

      // DEBUG
      //console.warn(`[RTK error] ${msg}${status ? ` (status: ${status})` : ''} → ${urlWithoutQuery}`);

      datadogRum.addError(err instanceof Error ? err : new Error(msg), {
        url: urlWithoutQuery,
        ...(status !== undefined ? { status: String(status) } : {})
      });
      return result;
    }

    // Success
    return result;
  };

  return BaseQueryWithTimeout;
};
